import { GridCellProps } from "@progress/kendo-react-grid";
import { usePageActions } from "../";
import { ProviderServiceModel } from "../../data/entities";
import { ProviderAddAllServicesRequest } from "../../models";
import { createODataQueryFilter, showErrorMessage, showSuccessMessage } from "../../utils";
import { PermissionDefinitions } from "../../security";
import { ProviderServiceService } from "../../services";
import { useCRUDHandlers, useGridActions, usePermission, useTenantFeatures } from "../../hooks";
import { AddProviderServiceModal, ManageProviderServiceModal } from "./components";
import {
  Breakpoints,
  Button,
  ConfirmationModal,
  Icon,
  IconAndText,
  IconDefinitions,
  ODataGrid,
  ResponsiveGridColumn,
  TitlelessCard,
} from "../../components";
import { useCallback } from "react";
import { useSharedState, useUrlParams } from "../../hooks";

export function ProviderServicesPage() {

  const gridData = useSharedState<Array<ProviderServiceModel>>([]);
  const providerServiceService = new ProviderServiceService();
  const locationId = parseInt(useUrlParams("id"));
  const providerId = parseInt(useUrlParams("providerId"));
  const { onClick, onSelect, showAddState, showEditState, showDeleteState, editItemState, deleteItemState, showDelete } = useGridActions<ProviderServiceModel>();
  const { gridRef, getItems, addItem, deleteItem, editItem } = useCRUDHandlers<ProviderServiceModel>(providerServiceService, "Provider Service", showAddState, showEditState);

  const { manageProviders } = useTenantFeatures();
  const canAddProviderService = usePermission(PermissionDefinitions.providerService.create(locationId!)) && manageProviders;
  const canDeleteProviderService = usePermission(PermissionDefinitions.providerService.delete(locationId!)) && manageProviders;

  usePageActions(
    canAddProviderService && (
      <Button
        onClick={() => {
          showAddState.setter(true);
        }}
      >
        <IconAndText iconName={IconDefinitions.add} text="Add" />
      </Button>
    )
  , [canAddProviderService]);

  const commandsCell = useCallback(
    (props: GridCellProps) => (
      <td className="text-center">
        <Icon
          iconName={IconDefinitions.delete}
          className="text-danger"
          onClick={() => showDelete(props.dataItem)}
        />
      </td>
    ),
    [showDelete]
  );
  const addAllServices = async (request: ProviderAddAllServicesRequest) => {
    try {
      const ok = await providerServiceService.insertAllServiceTypeServices(request);
      if (ok) {
        showSuccessMessage("services added successfully.");
        showAddState?.setter(false);
      }
    } catch (error) {
      showErrorMessage(`Error occurred while trying to execute insert: ${error}`);
    } finally {
      showAddState?.setter(false);
      gridRef.current?.refreshData();
    }
  }

  const durationCell = (props: GridCellProps) => {
    const item = props.dataItem as ProviderServiceModel;
    const duration = item.duration ?? item.service?.defaultDuration ?? "-";
    return (
      <td className={item.duration ? "fw-semibold" : "text-light-muted"}>
        {duration}
      </td>
    );
  };
  
  const bufferDurationCell = (props: GridCellProps) => {
    const item = props.dataItem as ProviderServiceModel;
    const buffer = item.bufferDuration ?? item.service?.defaultBufferDuration ?? "-";
    return (
      <td className={item.bufferDuration  ? "fw-semibold" : "text-light-muted"}>
        {buffer}
      </td>
    );
  };
  
  return (
    <TitlelessCard bodyClassName="p-0">
      <ODataGrid
        ref={gridRef}
        dataState={gridData}
        sortable={false}
        filterable={false}
        getData={(oDataQuery) => {
          const filterQuery = createODataQueryFilter(oDataQuery, [`providerId eq ${providerId}`]);
          return getItems(`${filterQuery}&$expand=service`)
        }}
        sort={[{ field: "service.name", dir: "asc"}]}
        onRowMenuSelect={onSelect}
        // Duration/buffer overrides stay editable regardless of the provider management toggle
        onRowClick={onClick}
        scrollable="scrollable"
        pageSize={10}
      >
        <ResponsiveGridColumn 
          title="Service"
          sortable={true}
          field="service.name"
          width={"auto"} 

        />
        <ResponsiveGridColumn
          title="Service Duration"
          sortable={true}
          field="service.defaultDuration"
          cell={durationCell}
          width={"auto"} 
          breakpoint={Breakpoints.md}
        />
        <ResponsiveGridColumn
          title="Buffer Duration"
          sortable={true}
          field="service.defaultBufferDuration"
          cell={bufferDurationCell}
          width={"auto"} 
          breakpoint={Breakpoints.lg}
        />
        {canDeleteProviderService === true && (
          <ResponsiveGridColumn
            className="column-url"
            field="url"
            title=" "
            headerCell={() => null}
            cell={commandsCell}
            width={50}
          />
        )}
      </ODataGrid>
      <AddProviderServiceModal
        showState={showAddState}
        providerId={providerId}
        addService={addItem}
        addServiceTypeServices={addAllServices}
      />
      <ManageProviderServiceModal
      dataItemState={editItemState}
      showState={showEditState}
      onSubmit={editItem}
      />
      <ConfirmationModal
        showState={showDeleteState}
        title={`Remove ${deleteItemState.value?.service?.name}`}
        message={`You are about to delete this service, it cannot be undone. Are you sure you want to delete ${deleteItemState.value.service?.name}?`}
        confirmText="Delete"
        onConfirm={() => deleteItem(deleteItemState.value)}
      />
    </TitlelessCard>
  );
}

export default ProviderServicesPage;

